import { Calendar, Clock, Users, FileText, Sparkles, ChevronRight, User } from 'lucide-react';
import type { MeetingCard as MeetingCardType } from '../types';

interface MeetingCardProps {
  meeting: MeetingCardType;
  onClick: () => void;
}

/**
 * MeetingCard Component
 * Toplantı listesindeki her bir toplantıyı gösterir
 */
export function MeetingCard({ meeting, onClick }: MeetingCardProps) {
  return (
    <div
      onClick={onClick}
      className="group relative bg-white rounded-2xl shadow-sm border border-slate-200/50 p-5 hover:shadow-xl hover:shadow-indigo-500/10 hover:border-indigo-200 transition-all duration-300 cursor-pointer overflow-hidden"
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* Content */}
      <div className="relative">
        {/* Badges */}
        <div className="flex gap-2 mb-3">
          {meeting.hasTranscript && (
            <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-sm">
              <FileText size={12} />
              Transkript
            </span>
          )}
          {meeting.hasAnalysis && (
            <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-sm">
              <Sparkles size={12} />
              Analiz
            </span>
          )}
          {!meeting.hasTranscript && !meeting.hasAnalysis && (
            <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-500 text-xs font-medium px-2.5 py-1 rounded-full">
              <Clock size={12} />
              Beklemede
            </span>
          )}
        </div>

        {/* Başlık */}
        <h3 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-2 mb-3 text-lg">
          {meeting.subject}
        </h3>

        {/* Meta bilgiler */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1.5 text-slate-500">
              <Calendar size={14} className="text-slate-400" />
              <span>{meeting.date}</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-500">
              <Clock size={14} className="text-slate-400" />
              <span>{meeting.time}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1.5 text-slate-500">
              <Users size={14} className="text-slate-400" />
              <span>{meeting.participantCount} katılımcı</span>
            </div>
            {meeting.duration && (
              <div className="text-slate-500">
                <span className="text-slate-400">Süre:</span> {meeting.duration}
              </div>
            )}
          </div>
        </div>

        {/* Organizatör */}
        <div className="flex items-center gap-2 text-sm">
          <div className="w-6 h-6 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
            <User size={12} className="text-white" />
          </div>
          <span className="text-slate-600">
            <span className="text-slate-400">Organizatör:</span>{' '}
            <span className="font-medium">{meeting.organizer}</span>
          </span>
        </div>

        {/* Önizleme (analiz varsa) */}
        {meeting.previewText && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-sm text-slate-500 line-clamp-2">
              {meeting.previewText}
            </p>
          </div>
        )}

        {/* Arrow indicator */}
        <div className="absolute top-1/2 -translate-y-1/2 right-2 opacity-0 group-hover:opacity-100 group-hover:right-4 transition-all duration-300">
          <ChevronRight size={20} className="text-indigo-400" />
        </div>
      </div>
    </div>
  );
}
